import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Inbox, Loader2, Mail, RefreshCw, X } from 'lucide-react';
import { api, type Note, type Proposal } from '../api.ts';
import { ago } from '../util.ts';
import { AuthorizeGoogle } from './AuthorizeGoogle.tsx';
import { openNoteInOmnibar } from './Omnibar.tsx';

type Watch = { proposals: Proposal[]; last_run: string | null; last_report: { checked: number; relevant: number; proposed: number; errors: string[] } | null; gmail: boolean };

// Proposals Gemini drafted from your own inbox. Only you see them; nothing changes until you Apply.
// The card only shows when there's something to decide; each proposal is two lines until you open its details.
export function InboxWatch() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['watch'], queryFn: () => api<Watch>('/watch'), refetchInterval: 120_000 });
  const run = useMutation({ mutationFn: () => api<{ proposed: number; checked: number; relevant: number; errors: string[] }>('/watch/run', { method: 'POST' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['watch'] }) });
  const apply = useMutation({
    mutationFn: (id: string) => api<{ note: Note }>(`/proposals/${id}/apply`, { method: 'POST' }),
    onSuccess: (d) => { qc.invalidateQueries(); openNoteInOmnibar(d.note); },
  });
  const dismiss = useMutation({ mutationFn: (id: string) => api(`/proposals/${id}/dismiss`, { method: 'POST' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['watch'] }) });
  const [open, setOpen] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const w = q.data;
  if (!w || (w.gmail && !w.proposals.length && !run.data && !run.isPending)) return null;
  const shown = all ? w.proposals : w.proposals.slice(0, 3);
  const busy = apply.isPending || dismiss.isPending;
  return (
    <section className="card inbox-watch">
      <div className="card-h">
        <h2><Inbox size={14} aria-hidden /> From your inbox{w.proposals.length > 0 && <span className="count">{w.proposals.length}</span>}</h2>
        {w.gmail && <button className="btn sm" onClick={() => run.mutate()} disabled={run.isPending} title="Check your recent mail for anything that changes the plan">
          {run.isPending ? <Loader2 size={13} className="spin" aria-hidden /> : <RefreshCw size={13} aria-hidden />} Check</button>}
      </div>
      {!w.gmail ? <p className="muted sm">Ops can watch your inbox for vendor and partner updates. <AuthorizeGoogle back="/" /></p> : (
        <>
          {w.proposals.length === 0 && <p className="muted sm">Nothing waiting. Ops checks your mail every hour for emails that change the plan{w.last_run ? ` (last checked ${ago(w.last_run)})` : ''}.</p>}
          <ul className="proposals">{shown.map((p) => (
            <li key={p.id} className={open === p.id ? 'open' : ''}>
              <p className="sm muted proposal-src"><Mail size={12} aria-hidden /> {p.email.from.replace(/<.*>/, '').trim() || p.email.from} · {ago(p.created_at)} · <a href={p.email.url} target="_blank" rel="noreferrer">{p.email.subject || '(no subject)'}</a></p>
              <p className="proposal-summary">{p.summary}</p>
              {open === p.id && p.lines.length > 0 && <ul className="proposal-lines">{p.lines.map((l, i) => <li key={i}>{l}</li>)}</ul>}
              <div className="row">
                <button className="btn sm primary" disabled={busy} onClick={() => apply.mutate(p.id)}>{apply.isPending && apply.variables === p.id ? <Loader2 size={13} className="spin" aria-hidden /> : <Check size={13} aria-hidden />} Apply</button>
                <button className="btn sm" disabled={busy} onClick={() => dismiss.mutate(p.id)}><X size={13} aria-hidden /> Dismiss</button>
                <button className="link sm proposal-more" aria-expanded={open === p.id} onClick={() => setOpen(open === p.id ? null : p.id)}>{open === p.id ? 'Less' : 'Details'} <ChevronDown size={12} aria-hidden /></button>
              </div>
            </li>
          ))}</ul>
          {w.proposals.length > 3 && <button className="link sm" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show all ${w.proposals.length}`}</button>}
          {run.data && <p className="muted sm">Checked {run.data.checked} new email{run.data.checked === 1 ? '' : 's'}: {run.data.relevant} touched the plan, {run.data.proposed} proposal{run.data.proposed === 1 ? '' : 's'}.{run.data.errors.length ? ` ${run.data.errors.join('; ')}` : ''}</p>}
          {(apply.error || run.error) && <p className="err sm">{((apply.error ?? run.error) as Error).message}</p>}
        </>
      )}
    </section>
  );
}
